import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getLivePulseSnapshot, getNationRivalry } from "@/lib/social.functions";
import { CinematicMomentsEngine, type CinematicMoment, type CinematicSignals } from "@/lib/cinematic/cinematicMomentsEngine";
import { recordWarRoomEvent } from "@/lib/warRoom/warRoomEngine";

const MAX_QUEUE = 4;

/**
 * Subscribes to live aggregates and emits cinematic moments.
 * Throttled by the engine; safe for millions concurrent (read-only aggregates).
 */
export function useCinematicMoments(extra?: Partial<CinematicSignals>) {
  const pulse = useQuery({
    queryKey: ["social", "pulse_snap"],
    queryFn: () => getLivePulseSnapshot(),
    refetchInterval: 15_000,
  });
  const rivalry = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 20_000,
  });

  const engine = useMemo(() => new CinematicMomentsEngine(), []);
  const [queue, setQueue] = useState<CinematicMoment[]>([]);
  const lastTickRef = useRef(0);

  useEffect(() => {
    const now = Date.now();
    if (now - lastTickRef.current < 1500) return;
    lastTickRef.current = now;

    const rows = rivalry.data?.rows ?? [];
    const totalHype = rows.reduce((acc, r) => acc + Number(r.hype_total ?? 0), 0);
    const top = rows[0];
    const signals: CinematicSignals = {
      activeFans: pulse.data?.active_fans ?? 0,
      hypeBurst: pulse.data?.hype_burst ?? 0,
      topNation: top?.nation_code ?? null,
      topNationShare: top && totalHype > 0 ? Number(top.hype_total) / totalHype : 0,
      ...extra,
    };
    const moment = engine.detect(signals, now);
    if (moment) {
      recordWarRoomEvent("cinematicMoments");
      setQueue((q) => [moment, ...q].slice(0, MAX_QUEUE));
    }
  }, [pulse.data, rivalry.data, engine, extra]);

  const dismiss = (id: string) => setQueue((q) => q.filter((m) => m.id !== id));

  return { moments: queue, dismiss, latest: queue[0] ?? null };
}